import React from "react";
import {
  DataGrid,
  DataGridProps,
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridFilterItem,
  GridLogicOperator,
  GridColDef,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { exportToExcel } from "../../helpers/exportToExcel";
import { removeIdKeysFromData } from "../../helpers/removeIdKeysFromData";
import useGridUserSettings from "../../hooks/useGridUserSettings";
import { applyGridFilterModel } from "./applyGridFilterModel";

const getOperatorsForColumn = (type?: string) => {
  if (type === "number") {
    return [
      { value: "=", label: "equals" },
      { value: "!=", label: "does not equal" },
      { value: ">", label: "greater than" },
      { value: ">=", label: "greater than or equal" },
      { value: "<", label: "less than" },
      { value: "<=", label: "less than or equal" },
      { value: "isEmpty", label: "is empty" },
      { value: "isNotEmpty", label: "is not empty" },
    ];
  }

  if (type === "date" || type === "dateTime") {
    return [
      { value: "is", label: "is" },
      { value: "not", label: "is not" },
      { value: "after", label: "is after" },
      { value: "onOrAfter", label: "is on or after" },
      { value: "before", label: "is before" },
      { value: "onOrBefore", label: "is on or before" },
      { value: "isEmpty", label: "is empty" },
      { value: "isNotEmpty", label: "is not empty" },
    ];
  }

  if (type === "singleSelect") {
    return [
      { value: "is", label: "is" },
      { value: "not", label: "is not" },
      { value: "isAnyOf", label: "is any of" },
      { value: "isEmpty", label: "is empty" },
      { value: "isNotEmpty", label: "is not empty" },
    ];
  }

  return [
    { value: "contains", label: "contains" },
    { value: "doesNotContain", label: "does not contain" },
    { value: "equals", label: "equals" },
    { value: "doesNotEqual", label: "does not equal" },
    { value: "startsWith", label: "starts with" },
    { value: "endsWith", label: "ends with" },
    { value: "isEmpty", label: "is empty" },
    { value: "isNotEmpty", label: "is not empty" },
  ];
};

const filterNeedsValue = (operator: string) =>
  operator !== "isEmpty" && operator !== "isNotEmpty";

const getColumnLabel = (column: any) => column.headerName || column.field;

type CompactViewConfig = {
  primaryField: string;
  secondaryFields?: string[];
  detailFields?: string[];
  actionFields?: string[];
  statusField?: string;
  getCardTitle?: (row: any) => React.ReactNode;
};

type PersistedDataGridProps = DataGridProps & {
  tableName: string;
  exportFileName?: string;
  sanitizeForExport?: (rows: any[]) => any[];
  exportRows?: any[];
  showExport?: boolean;
  showResetWidths?: boolean;
  showSummary?: boolean;
  applyFilterModel?: GridFilterModel;
  compactViewConfig?: CompactViewConfig;
  defaultCompactView?: boolean;
  mobileCompactView?: boolean;
};

const PersistedDataGrid: React.FC<PersistedDataGridProps> = ({
  tableName,
  rows,
  columns,
  exportFileName = tableName,
  sanitizeForExport = removeIdKeysFromData ?? ((r) => r),
  exportRows,
  showExport = true,
  showResetWidths = true,
  showSummary = true,
  applyFilterModel,
  compactViewConfig,
  defaultCompactView = false,
  mobileCompactView = false,
  slots,
  sx,
  ...rest
}) => {
  const settings = useGridUserSettings(tableName);

  const appliedFilterKeyRef = React.useRef<string | null>(null);
  const [multiFilterOpen, setMultiFilterOpen] = React.useState(false);
  const [draftFilterModel, setDraftFilterModel] =
    React.useState<GridFilterModel>({
      items: [],
      logicOperator: GridLogicOperator.And,
    });
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const compactControls = useMediaQuery(theme.breakpoints.down("md"));
  const [columnsOpen, setColumnsOpen] = React.useState(false);
  const [viewPreference, setViewPreference] = React.useState<boolean | null>(
    null,
  );
  const isCompactView =
    viewPreference ?? (defaultCompactView || (mobileCompactView && mobile));
  const toggleCompactView = () => setViewPreference(!isCompactView);

  React.useEffect(() => {
    if (!applyFilterModel) return;
    if (!settings.isFetched) return;

    const key = JSON.stringify(applyFilterModel);
    if (appliedFilterKeyRef.current === key) return;

    appliedFilterKeyRef.current = key;
    settings.setFilterModel(applyFilterModel);
  }, [applyFilterModel, settings]);

  const mergedColumns = React.useMemo(
    () =>
      columns.map((c) => ({
        ...c,
        width: settings.columnWidths[c.field] || c.width,
      })),
    [columns, settings.columnWidths],
  );

  const preFilteredRows = React.useMemo(
    () => applyGridFilterModel(rows as any[], columns, settings.filterModel),
    [rows, columns, settings.filterModel],
  );

  const summary = React.useMemo(() => {
    if (!showSummary) return null;
    const toLabel = (field: string) =>
      columns.find((c) => c.field === field)?.headerName || field;

    const sorted = settings.sortModel
      .map((s) => `${toLabel(s.field)} (${s.sort})`)
      .join(", ");

    const hidden = Object.keys(settings.columnVisibilityModel)
      .filter((f) => !settings.columnVisibilityModel[f])
      .map(toLabel)
      .join(", ");

    const filters = (settings.filterModel as GridFilterModel).items
      .map((f) => `${toLabel(f.field)}: ${f.value}`)
      .join(", ");

    return {
      sorted: sorted ? `Sorted by: ${sorted}` : "No sorting applied.",
      hidden: hidden ? `Hidden columns: ${hidden}` : "No hidden columns.",
      filtered: filters ? `Filtered by: ${filters}` : "No filtering applied.",
    };
  }, [
    showSummary,
    settings.sortModel,
    settings.columnVisibilityModel,
    settings.filterModel,
    columns,
  ]);

  const handleExport = () =>
    exportToExcel(
      sanitizeForExport((exportRows ?? preFilteredRows) as any[]),
      exportFileName,
    );

  const handleResetWidths = () =>
    settings.resetColumnWidths(columns.map((c) => c.field));

  const openMultiFilters = () => {
    setDraftFilterModel({
      ...settings.filterModel,
      logicOperator:
        settings.filterModel.logicOperator ?? GridLogicOperator.And,
      items: settings.filterModel.items.map((item, index) => ({
        ...item,
        id: item.id ?? `${item.field}-${index}`,
      })),
    });
    setMultiFilterOpen(true);
  };

  const addFilter = () => {
    const firstColumn = columns[0];
    if (!firstColumn) return;

    setDraftFilterModel((current) => ({
      ...current,
      items: [
        ...current.items,
        {
          id: `${Date.now()}-${current.items.length}`,
          field: firstColumn.field,
          operator: getOperatorsForColumn(firstColumn.type)[0].value,
          value: "",
        },
      ],
    }));
  };

  const updateFilter = (index: number, filter: GridFilterItem) => {
    setDraftFilterModel((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? filter : item,
      ),
    }));
  };

  const removeFilter = (index: number) => {
    setDraftFilterModel((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const applyMultiFilters = () => {
    settings.setFilterModel({
      ...draftFilterModel,
      items: draftFilterModel.items.map((item, index) => ({
        ...item,
        id: item.id ?? `${item.field}-${index}`,
      })),
    });
    setMultiFilterOpen(false);
  };

  const clearMultiFilters = () => {
    settings.setFilterModel({
      items: [],
      logicOperator: GridLogicOperator.And,
    });
    setDraftFilterModel({ items: [], logicOperator: GridLogicOperator.And });
    setMultiFilterOpen(false);
  };

  const getFormattedValue = React.useCallback(
    (row: any, column: GridColDef) => {
      const rawValue = row?.[column.field];
      const value = column.valueGetter
        ? column.valueGetter(rawValue as never, row, column, undefined as never)
        : rawValue;
      if (column.valueFormatter) {
        return column.valueFormatter(
          value as never,
          row,
          column,
          undefined as never,
        );
      }
      return value ?? "—";
    },
    [],
  );

  const getColumn = React.useCallback(
    (field: string) => columns.find((column) => column.field === field),
    [columns],
  );

  const renderCompactCell = React.useCallback(
    (row: any, field: string) => {
      const column = getColumn(field);
      if (!column) return null;

      if (
        (field === "notes" || field === "hrNotes") &&
        typeof row?.[field] === "string"
      ) {
        return (
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {row[field] || "—"}
          </Box>
        );
      }

      if (column.renderCell) {
        return column.renderCell({
          row,
          value: column.valueGetter
            ? column.valueGetter(
                row?.[field] as never,
                row,
                column,
                undefined as never,
              )
            : row?.[field],
          field,
          id: row?.id,
        } as any);
      }

      return getFormattedValue(row, column);
    },
    [getColumn, getFormattedValue],
  );

  const renderCompactField = React.useCallback(
    (row: any, field: string) => {
      const column = getColumn(field);
      if (!column) return null;

      return (
        <Box key={field} minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block">
            {getColumnLabel(column)}
          </Typography>
          <Box
            sx={{
              typography: "body2",
              fontWeight: 500,
              overflowWrap: "anywhere",
            }}
            title={String(getFormattedValue(row, column) ?? "")}
          >
            {renderCompactCell(row, field)}
          </Box>
        </Box>
      );
    },
    [getColumn, getFormattedValue, renderCompactCell],
  );

  const compactContainerSx = React.useMemo<SxProps<Theme>>(() => {
    const baseSx: SxProps<Theme> = {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    };

    if (!sx) {
      return baseSx;
    }

    return [
      baseSx,
      ...(Array.isArray(sx) ? sx : [sx]),
      ...(mobile ? [{ height: "auto", maxHeight: "none", minHeight: 0 }] : []),
    ];
  }, [sx, mobile]);

  const Toolbar = () => (
    <GridToolbarContainer
      sx={{
        display: { xs: "grid", sm: "flex" },
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1,
        p: 1,
        "& .MuiButton-root": {
          m: "0 !important",
          minWidth: 0,
          whiteSpace: "nowrap",
          px: 1,
          minHeight: { xs: 44, sm: 36 },
          border: { xs: "1px solid", sm: "none" },
          borderColor: "divider",
          fontSize: "0.8125rem",
        },
      }}
    >
      {compactControls ? (
        <Button
          startIcon={<ViewColumnIcon />}
          onClick={() => setColumnsOpen(true)}
          disabled={rest.disableColumnSelector}
        >
          Columns
        </Button>
      ) : (
        <GridToolbarColumnsButton />
      )}
      <Button onClick={openMultiFilters} sx={{ ml: 1 }}>
        Filters
      </Button>
      {showExport && (
        <Button onClick={handleExport} sx={{ ml: 1 }}>
          Export
        </Button>
      )}
      {showResetWidths && (
        <Button onClick={handleResetWidths} sx={{ ml: 1 }}>
          Reset widths
        </Button>
      )}
      {compactViewConfig && (
        <Button
          onClick={toggleCompactView}
          sx={{ ml: 1 }}
          startIcon={isCompactView ? <TableRowsIcon /> : <ViewAgendaIcon />}
        >
          {isCompactView ? "Table view" : "Card view"}
        </Button>
      )}
    </GridToolbarContainer>
  );

  const CompactToolbar = () => (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{
        display: { xs: "grid", sm: "flex" },
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        "& .MuiButton-root": {
          minHeight: 44,
          minWidth: 0,
          px: 1,
          whiteSpace: "nowrap",
        },
      }}
    >
      <Button variant="outlined" onClick={openMultiFilters}>
        Filters
      </Button>
      {showExport && (
        <Button variant="outlined" onClick={handleExport}>
          Export
        </Button>
      )}
      {showResetWidths && (
        <Button variant="outlined" onClick={handleResetWidths}>
          Reset widths
        </Button>
      )}
      {compactViewConfig && (
        <Button
          variant="outlined"
          onClick={toggleCompactView}
          startIcon={isCompactView ? <TableRowsIcon /> : <ViewAgendaIcon />}
        >
          {isCompactView ? "Table view" : "Card view"}
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      {showSummary && summary && (
        <Box mb={1} sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography variant="body2">{summary.sorted}</Typography>
          <Typography variant="body2">{summary.hidden}</Typography>
          <Typography variant="body2">{summary.filtered}</Typography>
        </Box>
      )}

      <Dialog
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="grid-columns-title"
      >
        <DialogTitle id="grid-columns-title">Visible columns</DialogTitle>
        <DialogContent dividers>
          <Stack>
            {columns.map((column) => (
              <FormControlLabel
                key={column.field}
                sx={{
                  mx: 0,
                  "& .MuiFormControlLabel-label": { overflowWrap: "anywhere" },
                }}
                label={getColumnLabel(column)}
                control={
                  <Checkbox
                    checked={
                      settings.columnVisibilityModel[column.field] !== false
                    }
                    disabled={column.hideable === false}
                    onChange={(_, checked) =>
                      settings.setColumnVisibilityModel({
                        ...settings.columnVisibilityModel,
                        [column.field]: checked,
                      })
                    }
                  />
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={multiFilterOpen}
        onClose={() => setMultiFilterOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Multi Filters</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            gap={2}
            flexWrap="wrap"
          >
            <Typography variant="body2">
              Records must match{" "}
              {draftFilterModel.logicOperator === GridLogicOperator.Or
                ? "any"
                : "all"}{" "}
              filter conditions.
            </Typography>
            <Select
              size="small"
              value={draftFilterModel.logicOperator ?? GridLogicOperator.And}
              onChange={(event) =>
                setDraftFilterModel((current) => ({
                  ...current,
                  logicOperator: event.target.value as GridLogicOperator,
                }))
              }
            >
              <MenuItem value={GridLogicOperator.And}>
                Match all filters
              </MenuItem>
              <MenuItem value={GridLogicOperator.Or}>Match any filter</MenuItem>
            </Select>
          </Box>

          {draftFilterModel.items.map((item, index) => {
            const column =
              columns.find((col) => col.field === item.field) ?? columns[0];
            const operators = getOperatorsForColumn(column?.type);
            const valueOptions = Array.isArray((column as any)?.valueOptions)
              ? ((column as any).valueOptions as any[])
              : [];

            return (
              <Box
                key={item.id ?? index}
                display="flex"
                gap={1}
                alignItems="center"
                mb={1}
                flexWrap="wrap"
                sx={{
                  "& > .MuiInputBase-root, & > .MuiTextField-root": {
                    flex: { xs: "1 1 100%", md: "1 1 180px" },
                  },
                }}
              >
                <Select
                  size="small"
                  value={item.field}
                  sx={{ minWidth: { xs: 0, sm: 180 } }}
                  onChange={(event) => {
                    const nextColumn =
                      columns.find((col) => col.field === event.target.value) ??
                      columns[0];
                    updateFilter(index, {
                      ...item,
                      field: nextColumn.field,
                      operator: getOperatorsForColumn(nextColumn.type)[0].value,
                      value: "",
                    });
                  }}
                >
                  {columns.map((col) => (
                    <MenuItem key={col.field} value={col.field}>
                      {getColumnLabel(col)}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  size="small"
                  value={item.operator}
                  sx={{ minWidth: { xs: 0, sm: 180 } }}
                  onChange={(event) =>
                    updateFilter(index, {
                      ...item,
                      operator: event.target.value,
                      value: filterNeedsValue(event.target.value)
                        ? (item.value ?? "")
                        : "",
                    })
                  }
                >
                  {operators.map((operator) => (
                    <MenuItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </MenuItem>
                  ))}
                </Select>

                {filterNeedsValue(item.operator) && valueOptions.length > 0 ? (
                  <Select
                    size="small"
                    value={item.value ?? ""}
                    sx={{ minWidth: { xs: 0, sm: 220 } }}
                    onChange={(event) =>
                      updateFilter(index, {
                        ...item,
                        value: event.target.value,
                      })
                    }
                  >
                    {valueOptions.map((option) => {
                      const value =
                        option && typeof option === "object"
                          ? option.value
                          : option;
                      const label =
                        option && typeof option === "object"
                          ? option.label
                          : option;
                      return (
                        <MenuItem key={String(value)} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                ) : (
                  <TextField
                    size="small"
                    type={
                      ["date", "dateTime"].includes(String(column?.type))
                        ? "date"
                        : "text"
                    }
                    value={item.value ?? ""}
                    disabled={!filterNeedsValue(item.operator)}
                    sx={{ minWidth: { xs: 0, sm: 220 } }}
                    onChange={(event) =>
                      updateFilter(index, {
                        ...item,
                        value: event.target.value,
                      })
                    }
                  />
                )}

                <IconButton
                  aria-label="Remove filter"
                  onClick={() => removeFilter(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}

          <Button onClick={addFilter}>Add Filter</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearMultiFilters}>Clear Filters</Button>
          <Button onClick={() => setMultiFilterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyMultiFilters}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {compactViewConfig && isCompactView ? (
        <Box sx={compactContainerSx}>
          <CompactToolbar />
          <Box
            minHeight={0}
            mt={1}
            sx={{
              flex: mobile ? "none" : 1,
              overflow: mobile ? "visible" : "auto",
            }}
          >
            <Stack spacing={1.5}>
              {preFilteredRows.map((row: any) => (
                <Card
                  key={row.id}
                  variant="outlined"
                  sx={
                    Array.isArray(rest.rowSelectionModel) &&
                    rest.rowSelectionModel.includes(row.id)
                      ? {
                          borderColor: "primary.main",
                          bgcolor: "action.selected",
                        }
                      : undefined
                  }
                >
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box minWidth={0} flex={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {compactViewConfig.getCardTitle
                            ? compactViewConfig.getCardTitle(row)
                            : renderCompactCell(
                                row,
                                compactViewConfig.primaryField,
                              )}
                        </Typography>
                        {!!compactViewConfig.secondaryFields?.length && (
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {compactViewConfig.secondaryFields.map((field) => (
                              <Typography
                                key={field}
                                variant="body2"
                                color="text.secondary"
                              >
                                {renderCompactCell(row, field)}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Box>
                      {compactViewConfig.statusField && (
                        <Box flexShrink={0}>
                          {renderCompactCell(
                            row,
                            compactViewConfig.statusField,
                          )}
                        </Box>
                      )}
                    </Stack>
                    {!!compactViewConfig.detailFields?.length && (
                      <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fit, minmax(min(140px, 100%), 1fr))"
                        gap={1}
                        mt={1.25}
                      >
                        {compactViewConfig.detailFields.map((field) =>
                          renderCompactField(row, field),
                        )}
                      </Box>
                    )}
                    {!!compactViewConfig.actionFields?.length && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {compactViewConfig.actionFields.map((field) => (
                            <Box
                              key={field}
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                "& > .MuiButton-root": { m: "0 !important" },
                              }}
                            >
                              {renderCompactCell(row, field)}
                            </Box>
                          ))}
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
              {!preFilteredRows.length && (
                <Box textAlign="center" py={4}>
                  <Chip label="No rows" />
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
      ) : (
        <DataGrid
          rows={preFilteredRows}
          columns={mergedColumns}
          sortModel={settings.sortModel}
          onSortModelChange={settings.setSortModel}
          columnVisibilityModel={settings.columnVisibilityModel}
          onColumnVisibilityModelChange={settings.setColumnVisibilityModel}
          onColumnResize={({ colDef, width }) =>
            settings.handleColumnResize(colDef.field, width)
          }
          sx={sx}
          slots={{ toolbar: Toolbar, ...slots }}
          getRowClassName={() => {
            return rest.onRowClick ? "MuiDataGrid-row--clickable" : "";
          }}
          {...rest}
        />
      )}
    </>
  );
};

export default PersistedDataGrid;
